import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as fromViewer from '@app/client/viewer/reducers/viewer.reducer';
import { MacroTaskWrapperService } from '@app/core/services/MacroTaskWrapper.service';
import { DomainService } from '@app/core/services/domain.service';
import { PromotionService } from '@app/core/services/promotion.service';
import * as fromRoot from '@app/reducers';
import { decodeRegionFromId, generateRandomString } from '@app/shared/helpers/helpers';
import { addMetadataToFile } from '@app/shared/helpers/transferFile';
import { genericRetry } from '@app/shared/operators/genericRetry';
import { retryOn500 } from '@app/shared/operators/retryOn500';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Link as LinkSdk } from '@smash-sdk-private/link/ejs/10-2019';
import { Region, SDKError } from '@smash-sdk/core';
import { CreateTransferSignedUrlsInput, GetTransferPreviewInput, ListTransferFilesPreviewInput, Transfer } from '@smash-sdk/transfer/ejs/01-2024';
import { DeviceDetectorService } from 'ngx-device-detector';
import { defer, from, of } from 'rxjs';
import { catchError, concatMap, map, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { ViewerActions } from '../actions';
import GetTargetError from '@smash-sdk-private/link/ejs/10-2019/types/GetTarget/GetTargetError';
import GetTransferPreviewError from '@smash-sdk/transfer/ejs/01-2024/types/GetTransferPreview/GetTransferPreviewError';
import ListTransferFilesPreviewError from '@smash-sdk/transfer/ejs/01-2024/types/ListTransferFilesPreview/ListTransferFilesPreviewError';
import CreateTransferSignedUrlsError from '@smash-sdk/transfer/ejs/01-2024/types/CreateTransferSignedUrls/CreateTransferSignedUrlsError';
import { UploadActions } from '@app/client/uploader/store/actions';
import { Gnitekram } from '@smash-sdk/gnitekram';
import moment from 'moment';
import { GoogleTagManagerService } from '@app/core/lib/google-tag-manager/angular-google-tag-manager.service';

@Injectable()
export class ViewerEffects {
    loadPreview$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ViewerActions.LoadPreview),
            withLatestFrom(
                this.store$.select(fromViewer.getPassword),
                this.store$.select(fromViewer.getAccessTracking),
            ),
            concatMap(([action, password, accessTracking]) => {
                const domainWithoutQueryString = this.domainService.detectDomainUrlWithoutQueryString();
                const linkSdk = new LinkSdk();
                return defer(() => linkSdk.getTarget({ targetId: domainWithoutQueryString })).pipe(
                    genericRetry({
                        retryConfig: [
                            ...Constant.NetworkErrorsRetryConfiguration,
                            { error: GetTargetError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
                            { error: GetTargetError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
                            { error: GetTargetError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
                        ]
                    }),
                    concatMap(({ target }) => {
                        if (target?.url) {

                            let getTransferPreviewCommonParameters: GetTransferPreviewInput = {
                                transferId: target.target,
                                "smash-authorization": `${encodeURIComponent(password)}`,
                            }

                            if (getEmailInQueryString() || accessTracking) {
                                getTransferPreviewCommonParameters = {
                                    ...getTransferPreviewCommonParameters,
                                    e: getEmailInQueryString() || btoa(unescape(encodeURIComponent(accessTracking))),
                                };
                            }

                            const decodedRegion = decodeRegionFromId(target.target);
                            const transferSdk = new Transfer({ region: decodedRegion as Region });
                            return defer(() => transferSdk.getTransferPreview(getTransferPreviewCommonParameters)).pipe(
                                genericRetry({
                                    retryConfig: [
                                        ...Constant.NetworkErrorsRetryConfiguration,
                                        { error: GetTransferPreviewError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
                                        { error: GetTransferPreviewError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
                                        { error: GetTransferPreviewError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
                                    ]
                                }),
                                concatMap(({ transfer, urlsExpiredAt }) => {
                                    if (transfer?.status === Constant.transferStatus.Uploaded || transfer?.status === Constant.transferStatus.Processing) {
                                        if (getEmailInQueryString()) {
                                            const username = decodeURIComponent(escape(atob(getEmailInQueryString())));
                                            return [
                                                ViewerActions.LoadPreviewSuccess({ preview: { transfer, urlsExpiredAt } }),
                                                ViewerActions.CheckFirstTimeReceiver({ username, transferId: transfer.id }),
                                            ];
                                        }
                                        return [
                                            ViewerActions.LoadPreviewSuccess({ preview: { transfer, urlsExpiredAt } }),
                                        ];
                                    } else if (transfer?.status === Constant.transferStatus.Suspended) {
                                        this.router.navigateByUrl('/suspended', { skipLocationChange: true });
                                        return of({ type: 'noop' });
                                    } else if (transfer?.status === Constant.transferStatus.Expired) {
                                        this.router.navigateByUrl('/expired', { skipLocationChange: true });
                                        return of({ type: 'noop' });
                                    } else if (transfer?.status === Constant.transferStatus.Deleted || transfer?.status === Constant.transferStatus.Deleting) {
                                        this.router.navigateByUrl('/deleted', { skipLocationChange: true });
                                        return of({ type: 'noop' });
                                    } else if (transfer?.status === Constant.transferStatus.Uploading) {
                                        this.router.navigateByUrl('/404', { skipLocationChange: true });
                                        return of({ type: 'noop' });
                                    }
                                }),
                                catchError((error: HttpErrorResponse) => {
                                    const errorMessage = error.message;
                                    const passwordRequired = error instanceof GetTransferPreviewError.PasswordDoesNotMatchError;
                                    const accessTrackingRequired = error instanceof GetTransferPreviewError.InvalidEmailError;
                                    if ((action.password && passwordRequired) || (action.accessTracking && accessTrackingRequired)) {
                                        const unlockPreviewErrorReason = passwordRequired ? 'WRONG_PASSWORD' : accessTrackingRequired ? 'EMAIL_INVALID' : null;
                                        return [
                                            ViewerActions.LockPreview({ passwordRequired, accessTrackingRequired }),
                                            ViewerActions.UnlockPreviewFailure({ errorType: unlockPreviewErrorReason }),
                                        ];
                                    } else if (accessTrackingRequired || passwordRequired) {
                                        return of(ViewerActions.LockPreview({ passwordRequired, accessTrackingRequired }));
                                    } else if (error instanceof GetTransferPreviewError.OwnerUsageExceededError
                                        || error instanceof GetTransferPreviewError.AccessorUsageExceededError) {
                                        this.router.navigateByUrl('/quota-exceeded', { skipLocationChange: true });
                                        return of(ViewerActions.LoadPreviewFailure({ error: error }));
                                    } else if (error instanceof GetTransferPreviewError.NotFoundError) {
                                        this.router.navigateByUrl('/404', { skipLocationChange: true });
                                        return of(ViewerActions.LoadPreviewFailure({ error: errorMessage }));
                                    } else {
                                        return of(ViewerActions.LoadPreviewFailure({ error: error }));
                                    }
                                })
                            );
                        }
                    }),
                    catchError((error: HttpErrorResponse) => {
                        if (error instanceof GetTargetError.NotFoundError) {
                            this.router.navigateByUrl('/404', { skipLocationChange: true });
                        }
                        return of(ViewerActions.LoadPreviewFailure({ error: error.message }));
                    })
                );
            })
        )
    );

    loadPreviewSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ViewerActions.LoadPreviewSuccess),
            map(action => action.preview),
            withLatestFrom(
                this.store$.select(fromRoot.getSelectedLanguage),
            ),
            concatMap(([preview]) => {

                if (preview?.transfer?.promotion?.url) {
                    if (this.deviceDetector.isMobile()) {
                        return [ViewerActions.LoadFiles()];
                    }
                    return [
                        ViewerActions.LoadPromotion({ promotionUrl: preview.transfer.promotion.url }),
                        ViewerActions.LoadFiles()
                    ];
                } else {
                    return [
                        ViewerActions.LoadFiles()
                    ];
                }
            })
        )
    );

    loadPromotion$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ViewerActions.LoadPromotion),
            map(action => action.promotionUrl),
            concatMap((promotionUrl) =>
                this.promotionService.loadPromotionByUrl(promotionUrl).pipe(
                    retryOn500(),
                    map(({ promotion }) => ViewerActions.LoadPromotionSuccess({ promotion })),
                    catchError((error: HttpErrorResponse) => of(ViewerActions.LoadPromotionFailure({ error: error.message }))),
                )
            )
        )
    );

    loadFiles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ViewerActions.LoadFiles),
            withLatestFrom(
                this.store$.select(fromViewer.getPassword),
                this.store$.select(fromViewer.getPreview),
                this.store$.select(fromRoot.getDevice),
                this.store$.select(fromViewer.getAccessTracking),
            ),
            concatMap(([action, password, preview, device, accessTracking]) => {
                const limit = (device.isTablet ? Constant.getMoreFilesLimit.tablet : Constant.getMoreFilesLimit.desktop);
                const decodedRegion = decodeRegionFromId(preview.transfer.id);
                const transferSdk = new Transfer({ region: decodedRegion as Region });
                const params: ListTransferFilesPreviewInput = {
                    transferId: preview.transfer.id,
                    limit,
                    "smash-authorization": `${encodeURIComponent(password)}`,
                };
                if (getEmailInQueryString() || accessTracking) {
                    params.e = getEmailInQueryString() || btoa(unescape(encodeURIComponent(accessTracking)));
                }
                return defer(() => transferSdk.listTransferFilesPreview(params)).pipe(
                    genericRetry({
                        retryConfig: [
                            ...Constant.NetworkErrorsRetryConfiguration,
                            { error: ListTransferFilesPreviewError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
                            { error: ListTransferFilesPreviewError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
                            { error: ListTransferFilesPreviewError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
                        ]
                    }),
                    map(({ files: newFiles, next, urlsExpiredAt }) => {
                        const batch = { id: generateRandomString(), next };
                        const isPreviewEnabled = preview.transfer.preview === 'Full';
                        const filesWithMetadata = newFiles.map((file, index) => ({ ...addMetadataToFile(file, isPreviewEnabled), expiry: urlsExpiredAt, index, batch }));
                        return ViewerActions.LoadFilesSuccess({ files: filesWithMetadata, next });
                    }),
                    catchError((error: HttpErrorResponse) => of(ViewerActions.LoadFilesFailure({ error: error.message })))
                );
            })
        )
    );

    loadMoreFiles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ViewerActions.LoadMoreFiles),
            withLatestFrom(
                this.store$.select(fromViewer.getPreview),
                this.store$.select(fromViewer.getPassword),
                this.store$.select(fromViewer.getFiles),
                this.store$.select(fromViewer.getNextFile),
                this.store$.select(fromRoot.getDevice),
                this.store$.select(fromViewer.getAccessTracking),
            ),
            concatMap(([action, preview, password, files, next, device, accessTracking]) => {
                const limit = (device.isTablet ? Constant.getMoreFilesLimit.tablet : Constant.getMoreFilesLimit.desktop);
                const filesAlreadyRetrievedLength = files.length;
                const transferSdk = new Transfer({ region: decodeRegionFromId(preview.transfer.id) as Region });

                const params: ListTransferFilesPreviewInput = {
                    transferId: preview.transfer.id,
                    start: next,
                    limit,
                    "smash-authorization": `${encodeURIComponent(password)}`,
                };
                if (getEmailInQueryString() || accessTracking) {
                    params.e = getEmailInQueryString() || btoa(unescape(encodeURIComponent(accessTracking)));
                }
                return defer(() => transferSdk.listTransferFilesPreview(params)).pipe(
                    genericRetry({
                        retryConfig: [
                            ...Constant.NetworkErrorsRetryConfiguration,
                            { error: ListTransferFilesPreviewError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
                            { error: ListTransferFilesPreviewError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
                            { error: ListTransferFilesPreviewError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
                        ]
                    }),
                    map(({ files: newFiles, next: newNext, urlsExpiredAt }) => {
                        const batch = { id: generateRandomString(), next };
                        const isPreviewEnabled = preview.transfer.preview === 'Full';
                        const filesWithMetadata = newFiles.map((file, index) => ({ ...addMetadataToFile(file, isPreviewEnabled), expiry: urlsExpiredAt, index: index + filesAlreadyRetrievedLength, batch }));
                        return ViewerActions.LoadMoreFilesSuccess({ files: filesWithMetadata, next: newNext });
                    }),
                    catchError((error: HttpErrorResponse) => of(ViewerActions.LoadMoreFilesFailure({ error: error.message })))
                )
            })
        )
    );

    refreshFiles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ViewerActions.RefreshFileAndAssociatedFiles),
            withLatestFrom(
                this.store$.select(fromViewer.getPassword),
                this.store$.select(fromViewer.getPreview),
                this.store$.select(fromViewer.getFiles),
                this.store$.select(fromViewer.getAccessTracking),
            ),
            concatMap(([action, password, preview, files, accessTracking]) => {
                const fileToRefresh = action.file;
                const associatedFiles = files.filter(file => file.batch.id === fileToRefresh.batch.id);
                const associatedFilesLength = associatedFiles.length;
                const transferSdk = new Transfer({ region: decodeRegionFromId(preview.transfer.id) as Region });

                const params: ListTransferFilesPreviewInput = {
                    transferId: preview.transfer.id,
                    start: fileToRefresh.batch.next,
                    limit: associatedFilesLength,
                    "smash-authorization": `${encodeURIComponent(password)}`,
                };
                if (getEmailInQueryString() || accessTracking) {
                    params.e = getEmailInQueryString() || btoa(unescape(encodeURIComponent(accessTracking)));
                }

                return defer(() => transferSdk.listTransferFilesPreview(params)).pipe(
                    genericRetry({
                        retryConfig: [
                            ...Constant.NetworkErrorsRetryConfiguration,
                            { error: ListTransferFilesPreviewError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
                            { error: ListTransferFilesPreviewError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
                            { error: ListTransferFilesPreviewError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
                        ]
                    }),
                    map(({ files: newFilesRetrieved, urlsExpiredAt }) => {
                        const newFilesToReturn = newFilesRetrieved.map(newFileRetrieved => {
                            const oldFile = files.find(oldFile => newFileRetrieved.id === oldFile.id);
                            const newFilesWithMetadata = { ...oldFile, ...newFileRetrieved, expiry: urlsExpiredAt };
                            return newFilesWithMetadata;
                        });
                        return ViewerActions.RefreshFileAndAssociatedFilesSuccess({ files: newFilesToReturn });
                    }),
                    catchError((error: HttpErrorResponse) => of(ViewerActions.RefreshFileAndAssociatedFilesFailure({ error: error.message })))
                )
            })
        )
    );

    requestDownloadUrls$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ViewerActions.RequestDownloadUrls),
            withLatestFrom(
                this.store$.select(fromViewer.getPreview),
                this.store$.select(fromViewer.getFiles),
                this.store$.select(fromViewer.getSelectedFiles),
                this.store$.select(fromViewer.getPassword),
                this.store$.select(fromViewer.getAccessTracking),
            ),
            concatMap(([action, preview, files, selectedFiles, password, accessTracking]) => {
                let createTransferSignedUrlsCommonParameters: CreateTransferSignedUrlsInput = {
                    transferId: preview.transfer.id,
                    "smash-authorization": `${encodeURIComponent(password)}`,
                }

                if (getEmailInQueryString() || accessTracking) {
                    createTransferSignedUrlsCommonParameters = {
                        ...createTransferSignedUrlsCommonParameters,
                        e: getEmailInQueryString() || btoa(unescape(encodeURIComponent(accessTracking))),
                    };
                }

                const transferSdk = new Transfer({ region: decodeRegionFromId(preview.transfer.id) as Region });
                // In case we just need to download one precise file (modal preview)
                if (action.file) {
                    const filesToGet = [{ id: action.file.id }];

                    return defer(() => transferSdk.createTransferSignedUrls({
                        ...createTransferSignedUrlsCommonParameters,
                        files: filesToGet,
                    })).pipe(
                        genericRetry({
                            retryConfig: [
                                ...Constant.NetworkErrorsRetryConfiguration,
                                { error: CreateTransferSignedUrlsError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
                                { error: CreateTransferSignedUrlsError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
                                { error: CreateTransferSignedUrlsError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
                            ]
                        }),
                        map(({ transfer }) => ViewerActions.RequestDownloadUrlsSuccess({ urls: transfer.files.map(file => file.url) })),
                        catchError((error: HttpErrorResponse) => of(ViewerActions.RequestDownloadUrlsFailure({ error: error.message })))
                    );

                } else {
                    const transferFilesNumber = preview?.transfer?.filesNumber;
                    if (transferFilesNumber === 1) {
                        const filesToGet = files.map(file => ({ id: file.id }));
                        return defer(() => transferSdk.createTransferSignedUrls({
                            ...createTransferSignedUrlsCommonParameters,
                            files: filesToGet,
                        })).pipe(
                            genericRetry({
                                retryConfig: [
                                    ...Constant.NetworkErrorsRetryConfiguration,
                                    { error: CreateTransferSignedUrlsError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
                                    { error: CreateTransferSignedUrlsError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
                                    { error: CreateTransferSignedUrlsError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
                                ]
                            }),
                            map(({ transfer }) => ViewerActions.RequestDownloadUrlsSuccess({ urls: transfer.files.map(file => file.url) })),
                            catchError((error: HttpErrorResponse) => of(ViewerActions.RequestDownloadUrlsFailure({ error: error.message })))
                        );
                    } else if (transferFilesNumber > 1) {
                        if (selectedFiles?.length > 0) {
                            const filesToGet = selectedFiles.map(file => ({ id: file.id }));
                            return defer(() => transferSdk.createTransferSignedUrls({
                                ...createTransferSignedUrlsCommonParameters,
                                files: filesToGet,
                            })).pipe(
                                genericRetry({
                                    retryConfig: [
                                        ...Constant.NetworkErrorsRetryConfiguration,
                                        { error: CreateTransferSignedUrlsError.InternalServerError, maxRetry: 10, exponentialBackoff: true },
                                        { error: CreateTransferSignedUrlsError.GatewayTimeoutError, maxRetry: 10, exponentialBackoff: true },
                                        { error: CreateTransferSignedUrlsError.BadGatewayError, maxRetry: 10, exponentialBackoff: true },
                                    ]
                                }),
                                map(({ transfer }) => ViewerActions.RequestDownloadUrlsSuccess({ urls: transfer.files.map(file => file.url) })),
                                catchError((error: HttpErrorResponse) => of(ViewerActions.RequestDownloadUrlsFailure({ error: error.message })))
                            );
                        } else {
                            return from(transferSdk.createTransferSignedUrls({
                                ...createTransferSignedUrlsCommonParameters,
                                files: [],
                            })).pipe(
                                map(({ transfer }) => ViewerActions.RequestDownloadUrlsSuccess({ urls: [transfer.url] })),
                                catchError((error: HttpErrorResponse) => of(ViewerActions.RequestDownloadUrlsFailure({ error: error.message })))
                            );
                        }
                    }
                }
            })
        )
    );

    loadTransferMetas$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ViewerActions.LoadTransferMetas),
            concatMap(() => {
                const linkSdk = new LinkSdk();
                const targetId = this.domainService.detectDomainUrlWithoutQueryString();
                // https://dev.to/krisplatis/angular-ssr-v16-saying-goodbye-to-a-sneaky-trick-macrotask-wrapping-for-http-calls-487b
                return this.macroTaskWrapper.wrapMacroTask("linkSdk.getTarget", linkSdk.getTarget({ targetId }))
                    .then(({ target }) => ViewerActions.LoadTransferInternal({ transferId: target.target, region: target.region }))
                    // TO FIX ? if request fail it wont be catched here.. it will be silent
                    .catch((error: SDKError) => ViewerActions.LoadTransferMetasFailure({ error: error.code }));
            })
        )
    );

    loadTransferInternal$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ViewerActions.LoadTransferInternal),
            concatMap(({ transferId, region }) => {
                const transferSdk = new Transfer({ region: region as Region });
                return this.macroTaskWrapper.wrapMacroTask("transferSdk.getTransferInternal", transferSdk.getTransferInternal({ transferId }))
                    .then(({ transfer }) => ViewerActions.LoadTransferMetasSuccess({ transfer: { ...transfer, targetPath: this.domainService.detectPath().slice(1) } }))
                    .catch((error: SDKError) => ViewerActions.LoadTransferMetasFailure({ error: error.code }));
            })
        )
    );

    checkFirstTimeReceiver$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ViewerActions.CheckFirstTimeReceiver),
            withLatestFrom(
                this.store$.select(fromRoot.getActiveIdentity),
            ),
            concatMap(([action, identity]) => {
                if (!identity?.username) {
                    const marketingSdk = new Gnitekram();
                    const params = { id: unescape(encodeURIComponent(action.username)), key: action.transferId };
                    return defer(() => marketingSdk.getUsage(params)).pipe(
                        map(({ usage }) => {
                            const isWithinLast14Days = usage?.firstReceiveDate && moment(usage.firstReceiveDate).isAfter(moment().subtract(14, 'days'));
                            if (usage?.firstReceiveDate && isWithinLast14Days) {
                                this.gtmService.pushTag({ event: Constant.eventGTM.FIRST_TIME_RECEIVER });
                            }
                            return ViewerActions.CheckFirstTimeReceiverSuccess({ usage });
                        }),
                        catchError((error) => {
                            return of(UploadActions.CheckFirstTimeSenderFailure({ error }));
                        })
                    );
                }
                return of({ type: 'noop' });
            }
            )
        )
    );

    constructor(
        private macroTaskWrapper: MacroTaskWrapperService,
        private actions$: Actions,
        private readonly store$: Store<fromRoot.State & fromViewer.State>,
        private domainService: DomainService,
        private promotionService: PromotionService,
        private deviceDetector: DeviceDetectorService,
        private gtmService: GoogleTagManagerService,
        private router: Router,
    ) { }
}

function getEmailInQueryString() {
    const uRLSearchParams = new URLSearchParams(window.location.search);
    return uRLSearchParams.get('e');
}